import {
  useMutation,
  useQueryClient,
  UseMutationOptions,
  QueryKey,
} from "@tanstack/react-query";
import { AxiosError } from "axios";
import { z } from "zod";
import { toast } from "sonner";

interface ApiMutationMeta<TData = unknown, TVariables = void>
  extends Record<string, unknown> {
  loadingMessage?: string;
  successMessage?: string | ((data: TData) => string);
  errorMessage?: string | ((error: ApiError) => string);
  invalidateQueries?:
    | QueryKey[]
    | ((data: TData, variables: TVariables) => QueryKey[]);
  schema?: z.ZodSchema;
  toastPosition?: "top-center" | "bottom-center" | "top-right" | "bottom-right";
  customToastStyles?: {
    title?: string;
    content?: string;
  };
  invalidateDelay?: number;
  refetchInterval?: number;
  errorDuration?: number;
}

interface MutationContext {
  toastId?: string | number;
}

type ApiError = AxiosError | z.ZodError | Error;

function getStringError(value: unknown): string | null {
  if (typeof value === "string" && value.trim()) return value;
  if (Array.isArray(value)) {
    const firstMessage = value.map(getStringError).find(Boolean);
    return firstMessage || null;
  }
  if (value && typeof value === "object") {
    const record = value as Record<string, unknown>;
    const preferredKeys = ["message", "error", "detail", "reason", "errors", "meta"];

    for (const key of preferredKeys) {
      const message = getStringError(record[key]);
      if (message) return message;
    }

    for (const [key, nestedValue] of Object.entries(record)) {
      if (["status", "statusCode", "code"].includes(key)) continue;
      const message = getStringError(nestedValue);
      if (message) return message;
    }
  }
  return null;
}

export function useApiMutation<
  TData = unknown,
  TError extends ApiError = ApiError,
  TVariables = void,
  TContext extends MutationContext = MutationContext,
>(
  mutationFn: (variables: TVariables) => Promise<TData>,
  options?: Omit<
    UseMutationOptions<TData, TError, TVariables, TContext>,
    "mutationFn" | "onMutate" | "onSuccess" | "onError" | "meta"
  > & {
    meta?: ApiMutationMeta<TData, TVariables>;
    onSuccess?: (
      data: TData,
      variables: TVariables,
      context: TContext,
    ) => void | Promise<void>;
    onError?: (
      error: TError,
      variables: TVariables,
      context: TContext | undefined,
    ) => void | Promise<void>;
  },
) {
  const queryClient = useQueryClient();
  const meta = options?.meta;

  return useMutation<TData, TError, TVariables, TContext>({
    ...options,
    mutationFn,
    onMutate: () => {
      const context = {} as TContext;
      if (meta?.loadingMessage) {
        const toastId = toast.loading(meta.loadingMessage, {
          position: meta.toastPosition || "top-center",
          classNames: meta.customToastStyles || {
            title: "font-outfit font-medium relative bottom-[0.6]",
            content: "flex items-center",
          },
        });

        (context as MutationContext).toastId = toastId;
      }
      return context;
    },
    onSuccess: (data, variables, context) => {
      if (meta?.successMessage) {
        const message =
          typeof meta.successMessage === "function"
            ? meta.successMessage(data)
            : meta.successMessage;
        if (context?.toastId) {
          toast.dismiss(context.toastId);
        }
        toast.success(message, { id: context?.toastId });
      }
      if (meta?.invalidateQueries) {
        const keys =
          typeof meta.invalidateQueries === "function"
            ? meta.invalidateQueries(data, variables)
            : meta.invalidateQueries;
        keys.forEach((queryKey: QueryKey) => {
          console.log("invalidating", queryKey);
          queryClient.invalidateQueries({ queryKey });
        });
      }

      options?.onSuccess?.(data, variables, context);
    },
    onError: (error, variables, context) => {
      let errorMessage = "Something went wrong";

      if (error instanceof AxiosError) {
        const data = error.response?.data;
        const backend = getStringError(data);

        if (backend) {
          errorMessage = backend;
        } else if (data) {
          errorMessage = JSON.stringify(data);
        } else {
          errorMessage = error.message;
        }
      }

      if (error instanceof z.ZodError) {
        errorMessage = "Validation error occurred";
      }

      if (meta?.errorMessage && errorMessage === "Something went wrong") {
        errorMessage =
          typeof meta.errorMessage === "function"
            ? meta.errorMessage(error)
            : meta.errorMessage;
      }

      toast.error(errorMessage, {
        id: context?.toastId,
        duration: meta?.errorDuration ?? 12000,
        closeButton: true,
      });
      options?.onError?.(error, variables, context);
    },
  });
}
